import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Paging,
  Select,
  Table,
  Tag,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
} from '@maxtropy/components';
import styles from './index.module.scss';
import React, { useState } from 'react';
import { Space } from 'antd';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';
import { useRequest } from 'ahooks';
import {
  apiV2MicrogridResolvedGetMicrogridConfigListPost,
  apiV2MicrogridResolvedGetMicrogridConfigPagePost,
  V2MicrogridResolvedGetMicrogridConfigPagePostResponse,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { useNavigate } from 'react-router-dom';

export interface FilterParams {
  ids?: number;
}
const columns = [
  {
    title: '微电网名称',
    width: 300,
    dataIndex: 'displayName',
    ellipsis: { showTitle: true },
    render: (v: string, record: itemProps) => (
      <EllipsisSpan value={`${record.displayGroupName}-${record.displayName}`} />
    ),
  },
  {
    title: '关联UET',
    width: 600,
    dataIndex: 'uetNameList',
    ellipsis: { showTitle: true },
    render: (v: any[]) => {
      return <EllipsisSpan value={v?.map((item, index) => <Tag key={index}>{`${item}`}</Tag>)} />;
    },
  },
];

export type itemProps = Exclude<V2MicrogridResolvedGetMicrogridConfigPagePostResponse['list'], undefined>[number];

const MicrogridStrategy = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();

  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<FilterParams>({});

  const hasSimulation = useHasPermission(PermissionsType.B_MICROGRID_POLICY_SIMULATION);
  const hasConfiguration = useHasPermission(PermissionsType.B_MICROGRID_POLICY_CONFIGURATION);

  // 查询列表
  const { data: pageList, loading } = useRequest(
    async () => {
      const res = await apiV2MicrogridResolvedGetMicrogridConfigPagePost({
        ...searchParams,
        ids: searchParams.ids ? [searchParams.ids] : undefined,
        page: pageOffset,
        size: pageSize,
      });
      setTotalCount(res.total!);
      return res?.list ?? [];
    },
    {
      refreshDeps: [pageOffset, pageSize, searchParams],
    }
  );
  // 查询列表
  const { data: microgridList } = useRequest(async () => {
    const res = await apiV2MicrogridResolvedGetMicrogridConfigListPost({});
    return res?.list ?? [];
  });

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 150,
      render: (_: any, record: itemProps) => {
        return (
          <Space size={10}>
            {hasConfiguration && (
              <Button
                type="link"
                onClick={() => navigate(`/microgrid/strategy/management/configuration?id=${record.configId}`)}
              >
                策略配置
              </Button>
            )}
            {hasSimulation && (
              <Button type="link" onClick={() => navigate(`/microgrids/strategy/simulation?id=${record.configId}`)}>
                策略仿真
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const onFinish = (val: FilterParams) => {
    setPageOffset(1);
    setSearchParams(val);
  };

  const onReset = () => {
    setPageOffset(1);
    setSearchParams({});
  };

  const filters = (
    <CustomFilter form={form} onFinish={(v: FilterParams) => onFinish(v)} onReset={onReset}>
      <Form.Item name="ids" label="微电网">
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="请选择"
          style={{ width: '100%' }}
          options={(microgridList ?? []).map(item => ({
            label: `${item.displayGroupName}-${item.displayName}`,
            value: item.dispatchId,
          }))}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <Table
        rowKey="id"
        sticky
        loading={loading}
        columns={buildColumns}
        dataSource={pageList ?? []}
        pagination={false}
      />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default MicrogridStrategy;
